<script setup>
import { watch, useTemplateRef, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import JobsearchSearchBar from './JobsearchSearchBar.vue';
import JobsearchDropdownBlockNearLocation from './JobsearchDropdownBlockNearLocation.vue';
import JobsearchDropdownBlockAutocompletions from './JobsearchDropdownBlockAutocompletions.vue';
import { queryTypes } from './constants.js';
import 'icons/geolocation.svg';
import { __ } from 'JIX/gettext';
import { useTwoWayBinding } from 'JIX/jobsearch/utils';
import JobsearchDropdownBlockFeaturedAreas from './JobsearchDropdownBlockFeaturedAreas.vue';

defineProps({
    inputClasses: {
        type: String,
        default: 'form-control form-control-lg',
    },
    showBadgeTypes: {
        type: Array,
        default: () => queryTypes,
    },
    autoFocus: {
        type: Boolean,
        default: false,
    },
    ariaLabel: {
        type: String,
        default: __('Hvor vil du arbejde?'),
    },
    placeholder: {
        type: String,
        default: __('Hvor vil du arbejde?'),
    },
});

const store = useStore();
const nearlocation = useTemplateRef('nearlocation');
const autocompletions = useTemplateRef('autocompletions');
const searchbar = useTemplateRef('searchbar');
const featuredareas = useTemplateRef('featuredareas');

const showDropdown = useTwoWayBinding('showDropdownGeo');
const userInput = useTwoWayBinding('userInputGeo');

function closeAndUnfocus(){
    showDropdown.value = false;
    searchbar.value?.unFocusInput();
}

// For when the dropdown opens and the content of the autocompletion dropdown changes.
watch([showDropdown, () => autocompletions.value?.hasContent], ([newShowDropdown, autcompletionHasContent]) => {
    // When autocomplete has content the two other blocks should hide.
    if (autcompletionHasContent) {
        autocompletions.value.show = true;
        nearlocation.value.show = false;
        featuredareas.value.show = false;
    // Fpr when the the dropdown is opened and autocompletion has no content.
    // This will also trigger when the dropdown is already opened and the autocompletion changes from content to no content.
    } else if (newShowDropdown && !autcompletionHasContent) {
        autocompletions.value.show = false;

        nearlocation.value.show = true;
        nearlocation.value.showNearLocationSubmenu = false;
        featuredareas.value.show = true;
    }
});

// For when the submenu in nearlocation opens.
watch(() => nearlocation.value?.showNearLocationSubmenu, (showNearLocationSubmenu) => {
    if (showNearLocationSubmenu) {
        featuredareas.value.show = false;
    } else {
        featuredareas.value.show = true;
    }
});

onMounted(() => {
    if (store.state.showDropdownGeo) {
        // To avoid the close button in the overlay getting focus.
        nextTick(() => {
            searchbar.value?.focusInput();
        });
    }
});

defineExpose({
    showDropdown,
    searchbar,
});

</script>

<template>
    <JobsearchSearchBar
        ref="searchbar"
        data-searchbar="geo"
        v-model:user-input="userInput"
        v-model:show-dropdown="showDropdown"
        :input-icon-settings="{ size: 'sm', name: 'geolocation'}"
        :input-classes="inputClasses"
        :aria-label="ariaLabel"
        :placeholder="placeholder"
    >
        <JobsearchDropdownBlockNearLocation
            ref="nearlocation"
            @close="closeAndUnfocus()"
        />
        <JobsearchDropdownBlockFeaturedAreas
            ref="featuredareas"
        />
        <JobsearchDropdownBlockAutocompletions
            ref="autocompletions"
            :completion-types="['geoareaid']"
        />
    </JobsearchSearchBar>
</template>
